import TableCell from '@mui/material/TableCell';
import React, { useMemo } from 'react';
import AchReturnedChip from '@/components/AchReturnedChip/AchReturnedChip';
import ReturnedChip from '@/components/ReturnedChip/ReturnedChip';
import {
  REFUND_METHOD,
  CHECK_TYPE_DB,
  CHECK_TYPE_LABELS,
  isPendingTransactionStatus,
  isReturnedAvailableToReclaimTransactionStatus,
  isReturnedPendingReturnToBankTransactionStatus,
  isReturnedTransactionStatus,
  isStartedTransactionStatus,
} from '@/utility';

const PaymentCategoryStatusCell = ({ refund, role, getTranslatedStatus }) => {
  const isAdmin = role === 'admin';
  const isReturned = isReturnedTransactionStatus(refund.transaction_status);
  const isReturnedPendingReturnToBank = isReturnedPendingReturnToBankTransactionStatus(
    refund.transaction_status,
  );
  const isReturnedAvailableToReclaim = isReturnedAvailableToReclaimTransactionStatus(
    refund.transaction_status,
  );
  const isPending = isPendingTransactionStatus(refund.transaction_status);
  const isStarted = isStartedTransactionStatus(refund.transaction_status);
  const showAchReturnedChip =
    isAdmin && isReturned && refund.refund_method === REFUND_METHOD.ACH;
  const showInstantPaymentReturnedChip =
    isAdmin &&
    isReturned &&
    refund.refund_method === REFUND_METHOD.INSTANT_PAYMENT;
  const showZelleReturnedChip =
    isAdmin && isReturned && refund.refund_method === REFUND_METHOD.ZELLE;
  const hasRefundMethod = Boolean(refund.refund_method);
  const hasTransactionStatus = Boolean(refund.transaction_status);
  const additionalInfo =
    refund?.additional_info || refund?.refund_additional_info;
  const hasImediatePayment = refund.pay_immediately;
  const immediateCheckType = refund.check_type;
  const immediateCheckTypeLabel =
    immediateCheckType === CHECK_TYPE_DB.ECHECK
      ? CHECK_TYPE_LABELS[CHECK_TYPE_DB.ECHECK]
      : CHECK_TYPE_LABELS[CHECK_TYPE_DB.PHYSICAL];
  const checkTypeDisplay =
    '(Immediate payment ' + immediateCheckTypeLabel + ')';

  const refundMethodDisplay = useMemo(() => {
    return hasRefundMethod ? refund.refund_method_string : 'N/A';
  }, [hasRefundMethod, refund.refund_method_string]);

  const transactionStatusDisplay = useMemo(() => {
    if (!hasTransactionStatus) {
      return null;
    }

    if (isReturned && !isAdmin) {
      return (
        <span
          style={{
            fontSize: '12px',
            color: '#ff0000',
          }}>
          {getTranslatedStatus(refund.transaction_status) || ' '}
        </span>
      );
    }
    if (
      !(
        showAchReturnedChip ||
        showInstantPaymentReturnedChip ||
        showZelleReturnedChip
      )
    ) {
      const transactionStatusColor = isReturned
        ? '#ff0000'
        : isReturnedAvailableToReclaim
        ? '#2e7d32'
        : isReturnedPendingReturnToBank
        ? '#ed6c02'
        : isPending
        ? '#ed6c02'
        : isStarted
        ? '#0288d1'
        : undefined;

      return (
        <span
          style={{
            fontSize: '12px',
            color: transactionStatusColor,
          }}>
          {getTranslatedStatus(refund.transaction_status) || ' '}
        </span>
      );
    }

    return null;
  }, [
    hasTransactionStatus,
    isReturned,
    isReturnedAvailableToReclaim,
    isReturnedPendingReturnToBank,
    isPending,
    isStarted,
    isAdmin,
    showAchReturnedChip,
    showInstantPaymentReturnedChip,
    showZelleReturnedChip,
    refund.transaction_status,
    getTranslatedStatus,
  ]);

  const shouldShowAdditionalInfo =
    ![REFUND_METHOD.VENMO, REFUND_METHOD.PAYPAL].includes(
      refund.refund_method,
    ) && additionalInfo;

  return (
    <TableCell
      align='center'
      data-testid={`transactionRowPaymentStatus-${refund.id}`}>
      {refundMethodDisplay}
      {refundMethodDisplay && <br />}

      {hasImediatePayment && (
        <span style={{ fontSize: '12px' }}>{checkTypeDisplay}</span>
      )}
      {hasImediatePayment && <br />}

      {showAchReturnedChip && <AchReturnedChip payment={refund} />}
      {showInstantPaymentReturnedChip && <ReturnedChip payment={refund} />}
      {showZelleReturnedChip && <ReturnedChip payment={refund} />}
      {(showAchReturnedChip ||
        showInstantPaymentReturnedChip ||
        showZelleReturnedChip) && <br />}

      {transactionStatusDisplay}

      {transactionStatusDisplay && <br />}

      {shouldShowAdditionalInfo && (
        <span style={{ fontSize: '12px' }}>({additionalInfo})</span>
      )}
    </TableCell>
  );
};

export default PaymentCategoryStatusCell;
